import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Button } from '@packages/utils';
import { ENotificationType } from '@idaho-aeyc/ui-kit';
import PageWrapper from 'components/Base/PageWrapper';
import CardContent from 'components/Base/Layout/Content/CardContent';
import { EIconNames } from 'components/Base/Icon';
import useTable from 'components/Base/Table/hooks/useTable';
import {
  useGetPeoplesListQuery,
  useExportPeopleMutation,
} from 'redux/apiSlice/people.slice';
import {
  ETableStoragePaths,
  TColumnsState,
} from 'components/Base/Table/Table.types';
import { Table } from 'components/Base/Table';
import { EPeoplesColumnKey, TPeoplesDataUI } from 'data/types/people.types';
import { TBackButtonState } from 'components/Base/PageWrapper/PageWrapper.types';
import CreateCoordinatorModal from 'components/Shared/Coordinator/CreateCoordinatorModal';
import { TCreateCoordinatorModalOpen } from 'components/Shared/Coordinator/CreateCoordinatorModal/CreateCoordinatorModal.types';
import { TRtkErrorType } from 'data/types/generic.types';
import { useGetTagsListQuery } from 'redux/apiSlice/tags.slice';
import { ETagType } from 'data/types/tags.types';
import ExportDropdown from 'components/Shared/ExportDropdown';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import { useShowToastNotification } from 'components/Base/Notification';
import useGetColumnsData from './PeopleList.data';

const COLUMN_KEY_TO_EXPORT_NAME: Record<string, string> = {
  [EPeoplesColumnKey.CREATION_DATE]: 'createdDate',
  [EPeoplesColumnKey.FIRST_NAME]: 'firstName',
  [EPeoplesColumnKey.LAST_NAME]: 'lastName',
  title: 'title',
  phone: 'phone',
  email: 'email',
  [EPeoplesColumnKey.TAGS]: 'tags',
};

const SORT_FIELD_MAP: Record<string, string> = {
  [EPeoplesColumnKey.CREATION_DATE]: 'CREATION_DATE',
  [EPeoplesColumnKey.FIRST_NAME]: 'FIRST_NAME',
  [EPeoplesColumnKey.LAST_NAME]: 'LAST_NAME',
};

const PeopleList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.people.list',
  });
  const { defaultColumns, defaultSortParam } = useGetColumnsData();
  const {
    page,
    pageSize,
    sortColumn,
    setPageSize,
    handleTableChange,
    selectedFilters,
    hasSelectedFilter,
    columns,
    setColumns,
    hasFilters,
    onFiltersReset,
    selectedRows,
    setSelectedRows,
  } = useTable<TPeoplesDataUI>({
    storagePath: ETableStoragePaths.PEOPLE,
    defaultColumns,
    defaultSortColumn: defaultSortParam,
  });

  const [openAddPesronModal, setOpenAddPesronModal] =
    useState<TCreateCoordinatorModalOpen>({
      visible: false,
      initialName: '',
    });

  const { data: tagsFilters } = useGetTagsListQuery(ETagType.PEOPLE);

  const { data, isFetching, isLoading, error } = useGetPeoplesListQuery({
    page,
    pageSize,
    sortColumn,
    filters: selectedFilters,
  });

  const onRowClick = (userId: string) => {
    navigate(`/app/people/${userId}`, {
      state: {
        backUrl: window.location.pathname,
        backButtonSuffix: t('TITLE'),
      } as TBackButtonState,
    });
  };

  useEffect(() => {
    setColumns(
      columns.map(item => {
        if (item.key === EPeoplesColumnKey.TAGS) {
          item.filters = tagsFilters;
        }
        return item;
      }),
    );
  }, [tagsFilters]);

  const isEmpty = !data?.content.length && !hasSelectedFilter && !isFetching;

  const [exportPeople, { isLoading: isExporting }] = useExportPeopleMutation();

  const { showToastNotification } = useShowToastNotification();

  const getVisibleExportColumns = useCallback(() => {
    const tableSettings = appLocalStorage.getItem(EStorageNames.tableSettings);
    const columnsState = tableSettings?.[
      ETableStoragePaths.PEOPLE
    ] as TColumnsState[];
    if (!columnsState) {
      return Object.values(COLUMN_KEY_TO_EXPORT_NAME);
    }
    return columnsState
      .filter(col => col.show)
      .map(col => COLUMN_KEY_TO_EXPORT_NAME[col.key as string])
      .filter(Boolean);
  }, []);

  const buildExportFilters = useCallback(
    (ids?: string[]) => {
      const filters: Record<string, unknown> = {};
      if (ids?.length) {
        filters.ids = ids;
      }
      if (selectedFilters?.firstName?.[0]) {
        filters.firstName = selectedFilters.firstName[0] as string;
      }
      if (selectedFilters?.lastName?.[0]) {
        filters.lastName = selectedFilters.lastName[0] as string;
      }
      if (selectedFilters?.email?.[0]) {
        filters.email = selectedFilters.email[0] as string;
      }
      if (selectedFilters?.tagIds) {
        filters.tags = selectedFilters.tagIds;
      }
      return Object.keys(filters).length > 0 ? filters : undefined;
    },
    [selectedFilters],
  );

  const buildExportSort = useCallback(() => {
    if (!sortColumn.sortField || !sortColumn.sortDirection) return undefined;
    const field = SORT_FIELD_MAP[sortColumn.sortField];
    if (!field) return undefined;
    return { field, direction: sortColumn.sortDirection };
  }, [sortColumn]);

  const handleExportAll = useCallback(async () => {
    try {
      const exportColumns = getVisibleExportColumns();
      await exportPeople({
        columns: exportColumns,
        ...(buildExportFilters() && { filters: buildExportFilters() }),
        ...(buildExportSort() && { sort: buildExportSort() }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportPeople,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const handleExportSelected = useCallback(async () => {
    const { selectedRowsKeys } = selectedRows;

    if (!selectedRowsKeys?.length) {
      showToastNotification({
        message: 'Please select at least one row to export',
        type: ENotificationType.ERROR,
      });
      return;
    }

    try {
      const ids = selectedRowsKeys.map(key => String(key));
      const exportColumns = getVisibleExportColumns();
      await exportPeople({
        columns: exportColumns,
        filters: buildExportFilters(ids),
        ...(buildExportSort() && { sort: buildExportSort() }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportPeople,
    selectedRows,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const headerActions = useMemo(
    () => [
      <ExportDropdown
        key="export-actions"
        onExportAll={handleExportAll}
        onExportSelected={handleExportSelected}
        isExporting={isExporting}
      />,
    ],
    [handleExportAll, handleExportSelected, isExporting],
  );

  return (
    <>
      <PageWrapper
        title={t('TITLE')}
        actions={
          <Button
            onClick={() =>
              setOpenAddPesronModal({
                visible: true,
              })
            }
            color="primary"
            type="primary"
          >
            {t('actions.add-person.ADD_PERSON_BTN')}
          </Button>
        }
        isLoading={isLoading}
        error={error as TRtkErrorType}
        emptyWrapper={{
          isEmpty,
          alt: 'People',
          icon: EIconNames.PEOPLE_PROFILE_FILLED,
          btnText: t('empty-view.BTN_TEXT'),
          emptyText: t('empty-view.EMPTY_TEXT'),
          onClick: () =>
            setOpenAddPesronModal({
              visible: true,
            }),
        }}
      >
        <CardContent>
          <Table<TPeoplesDataUI>
            rowKey={(record: TPeoplesDataUI) => record.userId}
            currentPage={page}
            columns={columns}
            dataSource={data?.content}
            rowClassName="pointer"
            loading={isFetching}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalDataCount={data?.totalElements}
            onChange={handleTableChange}
            hasFilters={hasFilters}
            onRow={(record: TPeoplesDataUI) => ({
              onClick: () => onRowClick(record.userId),
            })}
            onFiltersReset={onFiltersReset}
            storagePath={ETableStoragePaths.PEOPLE}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            headerActions={headerActions}
          />
        </CardContent>
      </PageWrapper>
      <CreateCoordinatorModal
        open={openAddPesronModal}
        setOpen={setOpenAddPesronModal}
        label={t('actions.add-person.modal.TITLE')}
      />
    </>
  );
};

export default PeopleList;
